import {
    CREATE_SECTION_IN_FORM,
    EDIT_SECTION_URL,
    get_headers,
    get_project_id,
    get_token,
    GET_QUESTIONS_IN_PROJECT,
    GET_FORM,
    GET_MAIN_SECTIONS,
    GET_SUBSECTION_BY_MAIN,
    ASSIGN_QUESTIONS_TO_FORM_SECTION,
    UNASSIGN_QUESTIONS_TO_FORM_SECTION,
    CREATE_MAIN_SECTIONS,
    UPDATE_MAIN_SECTION,
    REORDER_FORM_SECTION,
    REORDER_MAIN_SECTION,
    DELETE_MAIN_SECTION,
    DELETE_SECTION_URL,
} from "@/utils/constants/constants";
import alert_mixin from "@/utils/mixins/alert_mixin";
import drag_and_drop_mixin from "@/utils/mixins/drag_and_drop_mixin";
import formatting_mixins from "@/utils/mixins/formatting_mixins";
import axios from "axios";

export default {
  name: "MainSectionsWithSubsections",
  mixins: [alert_mixin, formatting_mixins, drag_and_drop_mixin],

  data: () => ({
    // auth / ids
    token: get_token(),
    headers: get_headers(),
    project_id: get_project_id(),
    form_id: "",

    // accordion data
    main_sections: [],
    current_main_section: null,

    // create sub-section
    new_section_name: "",
    section_name_error: false,

    // edit sub-section
    show_edit_section_modal: false,
    current_edit_section: { id: null, name: "", order: null },
    general_error: "",

    // assign questions
    assign_section_id: null,
    is_loading_questions: false,
    question_search: "",
    question_list: [],
    new_main_section: { name: "", description: "" },
    main_section_name_error: false,
    order_number_error: false,

    // Edit main section
    show_edit_main_section_modal: false,
    current_edit_main_section: { id: null, name: "", description: "" },

    current_reorder_section: null,
    current_reorder_section_index: null,
    current_reorder_sections: [],


  }),

  computed: {
    filtered_questions() {
      if (!this.question_search) return this.question_list;
      const q = this.question_search.toLowerCase();
      return this.question_list.filter(item =>
        (item.question_text || "").toLowerCase().includes(q) ||
        (item.question_type?.question_type || item.question_type || "").toLowerCase().includes(q) ||
        String(item.id).includes(q) ||
        String(item.question_number || "").includes(q)
      );
    },
  },

  methods: {
    toggle_main(index) {
        const sections = this.main_sections;
        if (!Array.isArray(sections)) return;
        if (!Number.isInteger(index) || index < 0 || index >= sections.length) return;

                const section = sections.at?.(index) ?? sections.find((_, i) => i === index);
        if (!section) return;

        section.expanded = !section.expanded;
        if (section.expanded && !section.loaded) {
            this.fetch_sub_sections(section.id);
        }
    },


    async get_main_sections() {
    const res = await axios.get(GET_MAIN_SECTIONS, {
        params: { form_id: this.form_id },
        headers: this.headers,
    });

    const rows = res?.data?.data ?? [];
    // Sort by order_number if available
    const sorted_rows = rows.sort((a, b) => {
        const a_order = a.order_number !== undefined ? Number(a.order_number) : 0;
        const b_order = b.order_number !== undefined ? Number(b.order_number) : 0;
        return a_order - b_order;
    });    
    // Map the data ensuring order_number is included
    this.main_sections = sorted_rows.map((m, index) => {
        const order_num = m.order_number !== undefined ? m.order_number : (index + 1);
        return {
            id: m.id,
            name: m.name,
            main_section_id: m.main_section_id,
            description: m.description,
            order_number: order_num, // Ensure this is properly set
            expanded: true,
            loaded: false,
            sub_sections: [],
        };
    });

    // Immediately load sub-sections for each main section
    for (const main of this.main_sections) {
        await this.fetch_sub_sections(main.id);
    }
},


    async fetch_sub_sections(form_main_section_id) {
    const res = await axios.get(GET_SUBSECTION_BY_MAIN, {
        params: { form_main_section_id },
        headers: this.headers,
    });

    const rows = res?.data?.data ?? [];
    // Sort sub-sections by order_number
    const sorted_rows = rows.sort((a, b) => {
        const a_order = a.order_number !== undefined ? Number(a.order_number) : 0;
        const b_order = b.order_number !== undefined ? Number(b.order_number) : 0;
        return a_order - b_order;
    });
    
    const ms = (this.main_sections || []).find(m => m.id === form_main_section_id);
    if (!ms) return;

    ms.sub_sections = sorted_rows.map((r, index) => {
        const order_num = r.order_number !== undefined ? r.order_number : (index + 1);
        return {
            id: r.section?.id,                 // Section.id
            name: r.section?.name,             // Section.name
            form_section_id: r.id,             // keep the FormSection (placement) id
            order: order_num, // Use API order or fallback to index
        };
    });

    ms.loaded = true;
},


    open_create_sub_section_modal(main) {
      this.current_main_section = main;
      this.new_section_name = "";
      this.section_name_error = false;
    },

    async create_sub_section() {
        if (!this.new_section_name || !this.new_section_name.trim()) {
            this.section_name_error = true;
            this.show_error("Sub-section name is required");
            return;
        }

        const form_main_section_id = this.current_main_section?.id;
        if (!form_main_section_id) {
            this.show_error("No main section selected");
            return;
        }

        const section_name = this.new_section_name.trim();

        const res = await axios.post(
            CREATE_SECTION_IN_FORM,
            {
                project_id: this.project_id,
                form_id: this.form_id,
                form_main_section_id: form_main_section_id,
                section_name: section_name,
            },
            { headers: this.headers }
        ).catch((error) => {
            let msg = error?.response?.data?.message || error.message || "Unknown error";

            if (typeof msg === 'string' && msg.startsWith('[') && msg.endsWith(']')) {
                msg = msg.slice(1, -1);
                msg = msg.replace(/['"]+/g, '');
            }

            this.show_error("Error creating sub-section: " + msg);
            return null;
        });


        if (!res) return;

        this.show_success(`Sub-section "${section_name}" created successfully`);
        this.new_section_name = "";
        await this.fetch_sub_sections(form_main_section_id);
    },

    open_reorder_main_sections_modal() {
      this.current_reorder_sections = [...(this.main_sections || [])];
    },

    update_main_section_order() {
        if (!Array.isArray(this.current_reorder_sections)) return;

        const payload = {
            form_id: this.form_id,
            reordered_main_sections: this.current_reorder_sections.map((section, index) => ({
                main_section_id: section.main_section_id,
                order_number: index + 1,
            })),
        }

        axios.post(REORDER_MAIN_SECTION, payload, {
            headers: this.headers,
        })
        .then(response => {
            const response_data = typeof response.data === "string" ? JSON.parse(response.data) : response.data;
            if (response_data?.status === "success") {
                this.show_success("Main sections reordered successfully");
                this.close_modal("reorder_main_sections_modal")
                return this.get_main_sections();
            } else {
                this.show_error("Failed to reorder main sections");
            } 
        })
        .catch(error => {
            this.show_error(error.response?.data?.message || error.message);
        });  
    },

    async delete_main_section(main_section_id) {

        const normalize_message = (m) => {
            if (!m && m !== 0) return null;
            if (Array.isArray(m)) return m.join(", ");
            if (typeof m === "string") {
                let s = m.trim();
                // If string looks like "['...']" or '["..."]' remove outer brackets then outer quotes
                if (s.startsWith("[") && s.endsWith("]")) {
                    s = s.slice(1, -1).trim();
                }
                s = s.replace(/^['"]|['"]$/g, "").trim();
                return s;
            }
            return String(m);
        };

        try {
            const res = await axios.post(
                DELETE_MAIN_SECTION,
                { 
                    main_section_id,
                    form_id: this.form_id,
                },
                { headers: this.headers }
            );
            const rd = typeof res.data === "string" ? JSON.parse(res.data) : res.data;
            const message = normalize_message(rd?.message ?? rd?.data);

            if (rd?.status === "success") {
                this.show_success(message || rd.message || "Main section deleted successfully");
                return this.get_main_sections();
            }
            this.show_error(message || rd?.message || "Failed to delete main section");
        } catch (error) {
            const raw = error.response?.data?.message ?? error.response?.data ?? error.message;
            const msg = normalize_message(raw) || "Failed to delete main section";
            this.show_error(msg);
        }
    },



    open_edit_sub_section_modal(section) {
      this.current_edit_section = {
        id: section.id,
        name: section.name,
        order: section.order || 1,
      };
      this.general_error = "";
      this.section_name_error = false;
      this.show_edit_section_modal = true;
    },

    async save_edited_section() {
        const id = this.current_edit_section?.id;
        if (!id) {
            this.show_error("Section ID is missing");
            return;
        }

        const name = this.current_edit_section.name?.trim();
        if (!name) {
            this.section_name_error = true;
            this.show_error("Sub-section name is required");
            return;
        }

        const res = await axios.put(
            EDIT_SECTION_URL,
            { 
                section_id: id, 
                name: name,
                order_number: this.current_edit_section.order
            },
            { headers: this.headers }
        ).catch((error) => {
            const msg = error?.response?.data?.message || error.message || "Failed to update sub-section";
            this.show_error(msg);
            return null; // stop further work
        });

        if (!res) return;

        const payload = (typeof res.data === "string") ? JSON.parse(res.data) : res.data;

        if (payload?.status === "success") {
            this.show_edit_section_modal = false;
            this.show_success(payload.message || "Sub-section updated successfully");

            const holder = (this.main_sections || []).find(ms =>
            (ms.sub_sections || []).some(s => s.id === id)
            );
            if (holder) await this.fetch_sub_sections(holder.id);
        } else {
            this.show_error(payload?.message || "Failed to update sub-section");
        }
    },


    delete_sub_section(section_id, main_section_id) {
        axios.post(
            DELETE_SECTION_URL,
            { 
                section_id,
                form_id: this.form_id
            },
            { headers: this.headers, }
        )
        .then(res => {
            const rd = typeof res.data === "string" ? JSON.parse(res.data) : res.data;
            if (rd?.status === "success") {
                this.show_success(rd.message || "Sub-section deleted successfully");
                return this.fetch_sub_sections(main_section_id);
            }
            this.show_error(rd?.message || "Failed to delete sub-section");
        })
        .catch(error => {
            const msg = error?.response?.data?.message || error.message || "Failed to delete sub-section";
            this.show_error(msg);
        });
    },



    async open_assign_modal(section_id) {
        this.assign_section_id = section_id;
        this.is_loading_questions = true;

        const [questions_response, form_response] = await Promise.all([
            axios.post(GET_QUESTIONS_IN_PROJECT, { project_id: this.project_id }, { headers: this.headers }),
            axios.get(GET_FORM, { headers: this.headers, params: { form_id: this.form_id } }),
        ]).catch((error) => {
            const msg = error?.response?.data?.message || error.message || "Unknown error";
            this.show_error("Error getting questions: " + msg);
            return [null, null];
        });

        if (!questions_response || !form_response) {
            this.question_list = [];
            this.is_loading_questions = false;
            return;
        }

        const all_questions =
            typeof questions_response.data === "string"
            ? JSON.parse(questions_response.data).data
            : questions_response.data?.data;
        const form_data =
            typeof form_response.data === "string"
            ? JSON.parse(form_response.data).data
            : form_response.data?.data;

        const current_section = (form_data?.sections || []).find(
            s => s.id === section_id || s.section_id === section_id
        );
        const assigned_questions = current_section?.questions || [];

        this.question_list = (all_questions || [])
            .filter(q => !q.is_assigned_as_dependent)
            .map(q => {
                const assigned = assigned_questions.find(
                    aq => aq.id === q.id || aq.question_text === q.question_text
                );
                return {
                    ...q,
                    is_assigned: !!assigned?.is_assigned,
                    is_loading: false
                };
            });

        this.is_loading_questions = false;
    },


    open_create_main_section_modal() {
        this.new_main_section = { name: "", description: "", order_number: null };
        this.main_section_name_error = false;
        this.order_number_error = false;
    },
    
    async create_main_section() {
        const name = (this.new_main_section?.name ?? "").trim();
        if (!name) {
            this.main_section_name_error = true;
            this.show_error("Main section name is required");
            return;
        }
        this.main_section_name_error = false;

        // order_number is required
        let order_number = this.new_main_section?.order_number;
        if (order_number === "" || order_number === undefined || order_number === null) {
            this.order_number_error = true;
            this.show_error("Order number is required");
            return;
        }
        const parsed_order_number = Number(order_number);
        const is_integer = Number.isInteger(parsed_order_number);
        if (!is_integer || parsed_order_number < 1) {
            this.order_number_error = true;
            this.show_error("Order number must be a positive whole number");
            return;
        }
        order_number = parsed_order_number;
        this.order_number_error = false;

        // normalize description: null if empty/whitespace
        const raw_description = this.new_main_section?.description ?? "";
        const trimmed_description = raw_description.trim();
        const main_section_description = trimmed_description.length ? trimmed_description : null;

        // build payload, omitting null optionals
        const payload = {
            form_id: Number(this.form_id),
            main_section_name: name,
            order_number: order_number,
        };
        if (main_section_description !== null){
            payload.main_section_description = main_section_description;
        }

        const res = await axios.post(CREATE_MAIN_SECTIONS, payload, { headers: this.headers })
            .catch((e) => {
                const msg = e?.response?.data?.message || e.message || "Unknown error";
                const formatted_message = msg.replace(/[^a-zA-Z0-9\s,.:]/g, '');
                this.show_error("Error creating main section: " + formatted_message);
                return null;
            });

        if (!res) return;

        this.show_success("Main section created successfully");
        this.close_modal("create_main_section_modal")
        await this.get_main_sections();

        // reset modal fields
        this.new_main_section = { name: "", description: "", order_number: null };
    },




    open_edit_main_section_modal(main) {
        this.current_edit_main_section = {
            id: main.id,
            main_section_id: main.main_section_id,
            name: main.name,
            description: main.description || "",
            order_number: main.order_number,
        };
        this.main_section_name_error = false;
        this.show_edit_main_section_modal = true;
    },

    async save_edited_main_section() {
        const main_section_id = this.current_edit_main_section.main_section_id;
        const name = (this.current_edit_main_section?.name ?? "").trim();
        if (!name) {
            this.main_section_name_error = true;
            this.show_error("Main section name is required");
            return;
        }
        this.main_section_name_error = false;

        const raw_desc = this.current_edit_main_section?.description ?? "";
        const trimmed_desc = raw_desc.trim();
        const main_section_description = trimmed_desc.length ? trimmed_desc : null;

        const payload = { main_section_id: main_section_id, main_section_name: name };
        if (main_section_description !== null) {
            payload.main_section_description = main_section_description;
        }

        const res = await axios.post(UPDATE_MAIN_SECTION, payload, { headers: this.headers })
            .catch((e) => {
            const msg = e?.response?.data?.message || e.message || "Unknown error";
            this.show_error("Error updating main section: " + msg);
            return null;
            });

        if (!res) return;

        const rd = res.data;
        if (rd?.status === "success") {
            this.show_success(rd.message || "Main section updated successfully");
            this.show_edit_main_section_modal = false;
            this.get_main_sections();
        } else {
            this.show_error(rd?.message || "Error updating main section");
        }
    },



    async on_toggle_assign(q) {
        if (q.is_loading) return;
        q.is_loading = true;

        const is_unassign = q.is_assigned;
        const url = is_unassign ? UNASSIGN_QUESTIONS_TO_FORM_SECTION : ASSIGN_QUESTIONS_TO_FORM_SECTION;
        const payload = is_unassign
            ? { project_id: this.project_id, form_id: this.form_id, form_section_id: this.assign_section_id, question_id: q.id }
            : { project_id: this.project_id, form_id: this.form_id, section_id: this.assign_section_id, question_ids: [q.id] };

        await axios.post(url, payload, { headers: this.headers })
            .then(() => {
            q.is_assigned = !is_unassign;
            this.show_success(is_unassign ? "Question unassigned successfully" : "Question assigned successfully");
            })
            .catch((error) => {
            const msg = error?.response?.data?.message || error.message || "Error updating assignment";
            this.show_error(msg);
            })
            .finally(() => {
            q.is_loading = false;
            });
    },

    update_section_order() {
        if (!Array.isArray(this.current_reorder_sections)) return;

        const reorderedSections = this.current_reorder_sections.map((section, index) => ({
            form_section_id: section.form_section_id,
            form_main_section_id: this.current_main_section.id,
            order_number: index + 1,
        }));

        axios.post(REORDER_FORM_SECTION, reorderedSections, {
            headers: this.headers,
        })
        .then(response => {
            const response_data = typeof response.data === "string" ? JSON.parse(response.data) : response.data;

            if (response_data?.status === "success") {
                this.show_success("Sections reordered successfully");
                this.close_modal("reorder_sections_modal")

                if (this.current_main_section?.id) {
                    return this.fetch_sub_sections(this.current_main_section.id);
                }
            } else {
                this.show_error("Failed to reorder sections");
            }
        })
        .catch(error => {
            this.show_error(error.response?.data?.message || error.message);
        });
    },

    open_reorder_modal(main) {
        this.current_main_section = main;
        this.current_reorder_sections = [...(main.sub_sections || [])];
    }

  },

  mounted() {
    this.form_id = this.$route.query.form_id;
    this.headers = get_headers();
    this.get_main_sections();
  },
};